import {AxiosError} from 'axios'
import {DefaultApiResponse} from 'models/ApiResponse'
import {IFormField} from 'models/Common'
import {
 CreateFestiveGreetingResponse,
 IFestiveGreeting,
 IFestiveGreetingDetails,
 IUpdateFestiveGreetingPayload,
 UpdateFestiveResponse,
} from 'models/festiveGreeting'
import {toast} from 'react-toastify'
import {get, patch, post, remove} from 'utils/axios'
import {handleRejectedErrorMsg} from 'utils/helpers/handleError'

type DeleteFestiveVariantResponse = {
 success: boolean
 message: string
 data: null
}

const fetchFestiveGreetings = async (
 page: number,
 pageSize: number,
 query?: IFormField,
 signal?: AbortSignal
): Promise<{data: IFestiveGreeting[]; total: number}> => {
 try {
  const params: IFormField = {
   page,
   page_size: pageSize,
   ...query,
  }
  const res = await get<DefaultApiResponse>(
   '/marketing/festive-greeting/list',
   params,
   {signal}
  )
  if (res?.success) {
   return {
    data: res.data?.results,
    total: res.data?.count,
   }
  }
  toast.error('Something went wrong')
  return {data: [], total: 0}
 } catch (err) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)

  return {data: [], total: 0}
 }
}

const updateFestiveApi = async (
 payload: IUpdateFestiveGreetingPayload
): Promise<{data: string | null}> => {
 try {
  const res = await patch<UpdateFestiveResponse>(
   `/marketing/festive-greeting/update`,
   payload
  )

  if (res?.success) {
   toast.success(
    res.message || 'Festive Greeting updated successfully'
   )
   return {data: res.message}
  }

  toast.error('Something went wrong')
  return {data: null}
 } catch (err) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return {data: null}
 }
}

const createFestiveGreetingApi = async (
 payload: Record<string, unknown>
): Promise<{success: boolean}> => {
 try {
  const res = await post<CreateFestiveGreetingResponse>(
   `/marketing/festive-greeting/create`,
   payload
  )

  if (res?.success) {
   toast.success(
    res.message || 'Festive Greeting created successfully'
   )
   return {success: true}
  }

  toast.error(res?.message || 'Something went wrong')
  return {success: false}
 } catch (err) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return {success: false}
 }
}

const fetchFestiveGreetingDetails = async (
 festiveGreetingId: number,
 signal?: AbortSignal
): Promise<IFestiveGreetingDetails | null> => {
 try {
  const res = await get<DefaultApiResponse>(
   '/marketing/festive-greeting/details',
   {festive_greeting_id: festiveGreetingId},
   {signal}
  )

  if (res?.success) {
   return res.data
  }

  toast.error(res?.message || 'Something went wrong')
  return null
 } catch (err) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const deleteFestiveVariantApi = async (
 festive_greeting_variant_id: number
): Promise<DeleteFestiveVariantResponse> => {
 try {
  const res = await remove<DeleteFestiveVariantResponse>(
   `/marketing/festive-greeting-variant/delete?festive_greeting_variant_id=${festive_greeting_variant_id}`,
   {}
  )

  if (res.success) {
   toast.success(
    res.message || 'Campaign variant deleted successfully'
   )
  } else {
   toast.error(res.message || 'Something went wrong')
  }

  return res
 } catch (err) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)

  return {
   success: false,
   message: 'Request failed',
   data: null,
  }
 }
}

const deleteFestiveApi = async (
 festive_greeting_id: number
): Promise<DeleteFestiveVariantResponse> => {
 try {
  const res = await remove<DeleteFestiveVariantResponse>(
   `/marketing/festive-greeting/delete?festive_greeting_id=${festive_greeting_id}`,
   {}
  )

  if (res.success) {
   toast.success(
    res.message || 'Campaign variant deleted successfully'
   )
  } else {
   toast.error(res.message || 'Something went wrong')
  }

  return res
 } catch (err) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)

  return {
   success: false,
   message: 'Request failed',
   data: null,
  }
 }
}

export {
 fetchFestiveGreetings,
 updateFestiveApi,
 createFestiveGreetingApi,
 fetchFestiveGreetingDetails,
 deleteFestiveVariantApi,
 deleteFestiveApi,
}
